import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MessageSquare, Send, Lock } from "lucide-react";
import { toast } from "sonner";
import { Page, Card, EmptyState } from "../components/UI";
import api from "../lib/api";

export default function Messages() {
  const [threads, setThreads] = useState([]);
  const [portalClients, setPortalClients] = useState([]);
  const [clients, setClients] = useState([]);
  const [active, setActive] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const end = useRef(null);

  const loadThreads = () => api.get("/messages/threads").then(({ data }) => { setThreads(data.threads); setPortalClients(data.portal_clients); });
  useEffect(() => { loadThreads(); api.get("/clients").then(({ data }) => setClients(data)); }, []);
  useEffect(() => {
    if (!active) return;
    const load = () => api.get(`/messages/${active}`).then(({ data }) => setMsgs(data));
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [active]);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    try {
      await api.post(`/messages/${active}`, { body: text });
      setText("");
      setMsgs((await api.get(`/messages/${active}`)).data);
      loadThreads();
    } catch (err) { toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Send failed"); }
  };

  const name = (id) => { const c = clients.find((x) => x.id === id); return c ? `${c.first_name} ${c.last_name}` : "Client"; };
  const startable = portalClients.filter((id) => !threads.some((t) => t.client_id === id));

  return (
    <Page title="Secure Messages" subtitle="Private, encrypted-in-transit messaging with clients who use the portal.">
      <div className="grid md:grid-cols-[300px_minmax(0,1fr)] gap-4">
        <Card testid="message-threads">
          <div className="space-y-1">
            {threads.map((t) => (
              <button key={t.client_id} onClick={() => setActive(t.client_id)} className="w-full text-left p-3 rounded-xl transition-colors" style={{ background: active === t.client_id ? "var(--mf-muted)" : "transparent" }} data-testid={`thread-${t.client_id}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium truncate">{t.name}</span>
                  {t.unread > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full text-white" style={{ background: "var(--mf-accent)" }}>{t.unread}</span>}
                </div>
                <div className="text-xs truncate" style={{ color: "var(--mf-text-muted)" }}>{t.last_sender === "clinician" ? "You: " : ""}{t.last_message}</div>
              </button>
            ))}
            {startable.length > 0 && <div className="text-xs pt-3 pb-1 px-1" style={{ color: "var(--mf-text-muted)" }}>Start a conversation</div>}
            {startable.map((id) => (
              <button key={id} onClick={() => setActive(id)} className="w-full text-left p-2 px-3 rounded-xl text-sm hover:bg-black/5" data-testid={`thread-start-${id}`}>{name(id)}</button>
            ))}
            {threads.length === 0 && startable.length === 0 && <EmptyState icon={MessageSquare} title="No conversations" description="Invite a client to the portal from their profile to start messaging." />}
          </div>
        </Card>
        <Card testid="message-conversation">
          {!active ? <div className="h-[55vh] flex items-center justify-center text-sm" style={{ color: "var(--mf-text-muted)" }}>Select a conversation</div> : (
            <>
              <div className="flex items-center justify-between mb-3">
                <Link to={`/clients/${active}`} className="font-serif text-xl" style={{ color: "var(--mf-primary)" }}>{name(active)}</Link>
                <span className="text-xs flex items-center gap-1" style={{ color: "var(--mf-text-muted)" }}><Lock className="w-3 h-3" /> Client gets an email alert without message content</span>
              </div>
              <div className="h-[50vh] overflow-y-auto space-y-2 p-3 rounded-xl" style={{ background: "var(--mf-muted)" }}>
                {msgs.map((m) => (
                  <div key={m.id} className={`flex ${m.sender === "clinician" ? "justify-end" : "justify-start"}`}>
                    <div className="max-w-[80%] px-4 py-2.5 rounded-2xl text-sm whitespace-pre-wrap" style={{ background: m.sender === "clinician" ? "var(--mf-primary)" : "var(--mf-surface)", color: m.sender === "clinician" ? "#FAFAF8" : "var(--mf-text)" }} data-testid="clinician-msg">
                      {m.body}
                      <div className="text-[10px] mt-1 opacity-60">{new Date(m.created_at).toLocaleString([], { dateStyle: "short", timeStyle: "short" })}</div>
                    </div>
                  </div>
                ))}
                <div ref={end} />
              </div>
              <form onSubmit={send} className="flex gap-2 mt-3">
                <input value={text} onChange={(e) => setText(e.target.value)} maxLength={5000} placeholder="Write a secure message…" className="flex-1 px-4 py-2.5 rounded-full text-sm outline-none" style={{ background: "var(--mf-muted)" }} data-testid="clinician-msg-input" />
                <button type="submit" className="mf-btn-primary px-4" aria-label="Send" data-testid="clinician-msg-send"><Send className="w-4 h-4" /></button>
              </form>
            </>
          )}
        </Card>
      </div>
    </Page>
  );
}
